import { useOutletContext } from 'react-router-dom';
import {
  Home, Building2, ShieldCheck, Handshake, Key, FileText, Phone, Mail, MapPin, MessageCircle, Clock,
} from 'lucide-react';
import { whatsappLink } from './portalApi';
import type { SiteInfo } from './portalApi';

function PageHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <section className="bg-gradient-to-br from-primary-800 to-primary-900 text-white">
      <div className="max-w-7xl mx-auto px-4 py-14">
        <h1 className="text-3xl md:text-4xl font-bold">{title}</h1>
        {subtitle && <p className="mt-2 text-primary-100 max-w-2xl">{subtitle}</p>}
      </div>
    </section>
  );
}

export function QuemSomos() {
  const { site } = useOutletContext<{ site: SiteInfo | null }>();
  const name = site?.name || 'Nossa imobiliária';
  return (
    <div>
      <PageHeader title="Quem Somos" subtitle={`Conheça a ${name}`} />
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-6 text-gray-700 leading-relaxed">
        {site?.content?.about ? (
          <div dangerouslySetInnerHTML={{ __html: site.content.about }} />
        ) : (
          <>
            <p>
              A <strong>{name}</strong> atua no mercado imobiliário com o compromisso de realizar sonhos,
              oferecendo atendimento próximo e transparente para quem deseja comprar, vender ou alugar um imóvel.
            </p>
            <p>
              Nossa equipe acompanha cada etapa do processo — da escolha do imóvel à assinatura do contrato —
              com segurança jurídica, avaliação justa e total dedicação ao cliente.
            </p>
          </>
        )}
        <div className="grid sm:grid-cols-2 gap-6 pt-4">
          {[
            { icon: Handshake, t: 'Atendimento humano', d: 'Pessoas reais cuidando do seu negócio.' },
            { icon: ShieldCheck, t: 'Segurança', d: 'Contratos e análises com respaldo jurídico.' },
            { icon: Building2, t: 'Portfólio amplo', d: 'Imóveis para venda e locação em diversas regiões.' },
            { icon: Home, t: 'Gestão completa', d: 'Administramos seu imóvel do início ao fim.' },
          ].map((f) => (
            <div key={f.t} className="flex gap-3">
              <div className="w-11 h-11 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                <f.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{f.t}</p>
                <p className="text-sm text-gray-500">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
        {site?.creci && <p className="text-sm text-gray-500 pt-4">CRECI {site.creci}</p>}
      </div>
    </div>
  );
}

export function Servicos() {
  const services = [
    { icon: Building2, t: 'Venda de imóveis', d: 'Divulgação, avaliação e intermediação completa da venda do seu imóvel.' },
    { icon: Key, t: 'Locação', d: 'Anúncio, seleção de inquilinos, análise de crédito e contrato de locação.' },
    { icon: FileText, t: 'Administração de aluguéis', d: 'Cobrança, repasse ao proprietário e gestão de contratos e cobranças.' },
    { icon: ShieldCheck, t: 'Análise e garantias', d: 'Análise cadastral, seguro fiança e segurança para locador e locatário.' },
    { icon: Home, t: 'Captação de imóveis', d: 'Avaliamos e captamos imóveis para venda e locação.' },
    { icon: Handshake, t: 'Assessoria', d: 'Orientação em toda a negociação, documentação e financiamento.' },
  ];
  return (
    <div>
      <PageHeader title="Serviços" subtitle="Soluções completas para o seu imóvel" />
      <div className="max-w-7xl mx-auto px-4 py-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {services.map((s) => (
          <div key={s.t} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="w-12 h-12 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
              <s.icon className="w-6 h-6" />
            </div>
            <p className="mt-4 font-semibold text-gray-900">{s.t}</p>
            <p className="mt-1 text-sm text-gray-500">{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Contato() {
  const { site } = useOutletContext<{ site: SiteInfo | null }>();
  const wa = whatsappLink(site?.whatsapp, `Olá! Gostaria de falar com a ${site?.name || 'imobiliária'}.`);
  const addr = site?.address;
  const fullAddress = addr
    ? [
        [addr.street, addr.number].filter(Boolean).join(', '),
        addr.neighborhood,
        [addr.city, addr.state].filter(Boolean).join('/'),
        addr.zipCode && `CEP ${addr.zipCode}`,
      ]
        .filter(Boolean)
        .join(' - ')
    : '';

  return (
    <div>
      <PageHeader title="Contato" subtitle="Fale com a nossa equipe" />
      <div className="max-w-5xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          {site?.phone && (
            <a href={`tel:${site.phone}`} className="flex items-center gap-3 text-gray-700 hover:text-primary-700">
              <span className="w-11 h-11 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </span>
              <span>{site.phone}</span>
            </a>
          )}
          {site?.email && (
            <a href={`mailto:${site.email}`} className="flex items-center gap-3 text-gray-700 hover:text-primary-700">
              <span className="w-11 h-11 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </span>
              <span>{site.email}</span>
            </a>
          )}
          {fullAddress && (
            <div className="flex items-center gap-3 text-gray-700">
              <span className="w-11 h-11 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5" />
              </span>
              <span>{fullAddress}</span>
            </div>
          )}
          <div className="flex items-center gap-3 text-gray-700">
            <span className="w-11 h-11 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </span>
            <span>{site?.content?.businessHours || 'Segunda a sexta, 8h às 18h · Sábado, 9h às 12h'}</span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex flex-col justify-center">
          <p className="font-semibold text-gray-900">Atendimento rápido</p>
          <p className="mt-1 text-sm text-gray-500">
            Prefere falar agora? Clique no botão e converse com a gente pelo WhatsApp.
          </p>
          {site?.whatsapp && (
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              className="mt-4 flex items-center justify-center gap-2 py-3 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
            >
              <MessageCircle className="w-5 h-5" /> Falar no WhatsApp
            </a>
          )}
          {site?.creci && <p className="mt-4 text-xs text-gray-400 text-center">CRECI {site.creci}</p>}
        </div>
      </div>
    </div>
  );
}

function LegalLayout({ title, children }: { title: string; children: any }) {
  return (
    <div>
      <PageHeader title={title} subtitle={`Última atualização: ${new Date().toLocaleDateString('pt-BR')}`} />
      <div className="max-w-3xl mx-auto px-4 py-12 text-gray-700 leading-relaxed space-y-4 text-justify [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-6 [&_p]:mt-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-primary-600 [&_a]:underline">
        {children}
      </div>
    </div>
  );
}

export function PoliticaPrivacidade() {
  const { site } = useOutletContext<{ site: SiteInfo | null }>();
  const c = site?.content || {};
  const nome = site?.name || 'A imobiliária';
  const email = c.dpoEmail || site?.email || 'contato@imobiliaria.com.br';
  if (c.privacy) {
    return (
      <LegalLayout title="Política de Privacidade">
        <div dangerouslySetInnerHTML={{ __html: c.privacy }} />
      </LegalLayout>
    );
  }
  return (
    <LegalLayout title="Política de Privacidade">
      <p>
        A <strong>{nome}</strong> respeita a sua privacidade e está comprometida com a proteção dos dados pessoais
        tratados em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD – Lei nº 13.709/2018)</strong>.
        Esta política explica como coletamos, usamos e protegemos suas informações.
      </p>
      <h2>1. Dados que coletamos</h2>
      <ul>
        <li>Dados de identificação e contato (nome, e-mail, telefone) informados em formulários ou no WhatsApp;</li>
        <li>Dados de interesse imobiliário (imóveis visualizados, preferências, mensagens enviadas);</li>
        <li>Dados de navegação (cookies, endereço IP, páginas acessadas) — veja a Política de Cookies.</li>
      </ul>
      <h2>2. Como usamos seus dados</h2>
      <ul>
        <li>Responder solicitações, agendar visitas e prestar atendimento;</li>
        <li>Apresentar imóveis e oportunidades compatíveis com o seu interesse;</li>
        <li>Cumprir obrigações legais, contratuais e de prevenção a fraudes;</li>
        <li>Melhorar a experiência de navegação no site.</li>
      </ul>
      <h2>3. Compartilhamento</h2>
      <p>
        Não vendemos seus dados. Podemos compartilhá-los com proprietários, parceiros e prestadores estritamente
        necessários à negociação imobiliária, bem como com autoridades quando exigido por lei.
      </p>
      <h2>4. Seus direitos (LGPD)</h2>
      <p>
        Você pode, a qualquer momento, solicitar confirmação do tratamento, acesso, correção, anonimização, portabilidade,
        eliminação dos dados e revogação do consentimento, escrevendo para <strong>{email}</strong>.
      </p>
      <h2>5. Segurança e retenção</h2>
      <p>
        Adotamos medidas técnicas e organizacionais para proteger seus dados, que são mantidos apenas pelo tempo
        necessário às finalidades informadas ou para cumprimento de obrigações legais.
      </p>
      <h2>6. Cookies</h2>
      <p>
        Utilizamos cookies para melhorar a navegação. Saiba mais na nossa Política de Cookies.
      </p>
      <h2>7. Encarregado pelo tratamento de dados (DPO)</h2>
      <p>
        {c.dpoName ? <>O Encarregado (DPO) é <strong>{c.dpoName}</strong>, </> : 'O Encarregado (DPO) '}
        responsável por atender solicitações relativas aos seus dados pessoais, e pode ser contatado pelo e-mail{' '}
        <strong>{email}</strong>.
      </p>
      <h2>8. Dados de crianças e adolescentes</h2>
      <p>
        Este site não é direcionado a menores de 18 anos e não coletamos intencionalmente dados de crianças e
        adolescentes sem o consentimento e a supervisão dos responsáveis legais. Caso identifiquemos tal coleta,
        os dados serão eliminados.
      </p>
      <h2>9. Alterações desta política</h2>
      <p>
        Esta política pode ser atualizada periodicamente. A versão vigente é sempre a publicada nesta página, com a
        respectiva data de atualização.
      </p>
      <h2>10. Contato</h2>
      <p>Dúvidas sobre esta política ou sobre seus dados? Fale com a gente: <strong>{email}</strong>.</p>
    </LegalLayout>
  );
}

export function PoliticaCookies() {
  const { site } = useOutletContext<{ site: SiteInfo | null }>();
  const nome = site?.name || 'A imobiliária';
  if (site?.content?.cookies) {
    return (
      <LegalLayout title="Política de Cookies">
        <div dangerouslySetInnerHTML={{ __html: site.content.cookies }} />
      </LegalLayout>
    );
  }
  return (
    <LegalLayout title="Política de Cookies">
      <p>
        O site da <strong>{nome}</strong> utiliza cookies para melhorar sua experiência de navegação, lembrar suas
        preferências e entender como o site é utilizado.
      </p>
      <h2>O que são cookies</h2>
      <p>
        Cookies são pequenos arquivos armazenados no seu dispositivo quando você visita um site. Eles permitem que o
        site funcione corretamente e ofereça uma navegação personalizada.
      </p>
      <h2>Tipos de cookies que usamos</h2>
      <ul>
        <li><strong>Necessários:</strong> essenciais para o funcionamento do site (não podem ser desativados);</li>
        <li><strong>Desempenho:</strong> ajudam a entender como os visitantes interagem com o site;</li>
        <li><strong>Funcionais:</strong> lembram preferências como imóveis favoritos;</li>
        <li><strong>Marketing:</strong> podem ser usados para exibir conteúdo e anúncios mais relevantes.</li>
      </ul>
      <h2>Como gerenciar</h2>
      <p>
        Você pode bloquear ou apagar cookies nas configurações do seu navegador. A desativação de alguns cookies pode
        afetar o funcionamento de partes do site.
      </p>
    </LegalLayout>
  );
}

export function TermosUso() {
  const { site } = useOutletContext<{ site: SiteInfo | null }>();
  const nome = site?.name || 'A imobiliária';
  const permuta = site?.content?.permuta;
  if (site?.content?.terms) {
    return (
      <LegalLayout title="Termos de Uso">
        <div dangerouslySetInnerHTML={{ __html: site.content.terms }} />
        {permuta && (
          <>
            <h2>Permuta</h2>
            <div dangerouslySetInnerHTML={{ __html: permuta }} />
          </>
        )}
      </LegalLayout>
    );
  }
  return (
    <LegalLayout title="Termos de Uso">
      <p>
        Ao acessar e utilizar o site da <strong>{nome}</strong>, você concorda com estes Termos de Uso. Caso não
        concorde, recomendamos não utilizar o site.
      </p>
      <h2>1. Uso do site</h2>
      <p>
        O conteúdo é disponibilizado para fins informativos sobre imóveis e serviços. As informações de imóveis
        (valores, características e disponibilidade) podem sofrer alterações sem aviso prévio e devem ser confirmadas
        com a nossa equipe antes de qualquer negociação.
      </p>
      <h2>2. Propriedade intelectual</h2>
      <p>
        Marca, logotipo, textos e imagens são de propriedade da {nome} ou de seus parceiros, sendo vedada a reprodução
        sem autorização.
      </p>
      <h2>3. Responsabilidades</h2>
      <p>
        Empenhamo-nos para manter as informações corretas e atualizadas, mas não garantimos a ausência de erros. A
        negociação de qualquer imóvel está sujeita à confirmação de dados, documentação e disponibilidade.
      </p>
      <h2>4. Alterações</h2>
      <p>Estes termos podem ser atualizados a qualquer momento, passando a valer a partir da publicação no site.</p>
      {permuta && (
        <>
          <h2>5. Permuta</h2>
          <div dangerouslySetInnerHTML={{ __html: permuta }} />
        </>
      )}
    </LegalLayout>
  );
}
